import styled from 'styled-components';

import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';
import { useLanguage } from '../../Context/LanguageContext';

import AgeGroupIcon from '../../image/AgeGroupIcon.svg';

import IntroMarkdown from './IntroMarkdown';

export default function EngagementChartRenderer(props) {
  const { data } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const {
    intro,
    allAgeGroups,
    engagementStates,
    engagementTotalCount,
    engagementMaxCount,
    engagementMaxKey,
    startDate,
    endDate,
  } = data;

  // 연령대 key → 다국어 라벨 매핑 //
  const keyToLabel = {
    under10: t('statistics.engagement.ageUnder10'),
    '10s': t('statistics.engagement.age10s'),
    '20s': t('statistics.engagement.age20s'),
    '30s': t('statistics.engagement.age30s'),
    '40s': t('statistics.engagement.age40s'),
    '50s': t('statistics.engagement.age50s'),
    '60s': t('statistics.engagement.age60s'),
    '70s': t('statistics.engagement.age70s'),
    '80s': t('statistics.engagement.age80s'),
    '90s': t('statistics.engagement.age90s'),
  };

  // 연령별 비율 계산 //
  function calcPercentage(count) {
    if (engagementTotalCount === 0) return '0';

    return (count / engagementTotalCount * 100).toFixed(1);
  }

  // 프로그레스 바 너비 비율 계산 //
  function calcBarWidth(count) {
    if (engagementMaxCount === 0) return 0;

    return Math.min((count / engagementMaxCount) * 100, 100);
  }

  // 선호 비중 계산 (분모는 해당 연령대의 reserve 누적 합 = reservedSum) //
  function calcPreference(usageCount, groupReservedSum) {
    if (groupReservedSum === 0) return '0';

    return (usageCount / groupReservedSum * 100).toFixed(0);
  }

  // 숫자 콤마 포맷팅 //
  function formatNumber(num) {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // 프로그레스 바 카드 렌더링 //
  function renderProgressBarCard(group, index) {
    const percentage = calcPercentage(group.totalCount);
    const barWidth = calcBarWidth(group.totalCount);
    const label = keyToLabel[group.key] || group.key;

    return (
      <ProgressBarCardWrapper key={'EngagementProgressBar-' + index}>
        <ProgressBarInfoRow>
          <ProgressBarLabelWrapper>
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{label}</Text>
            <PercentageWrapper>
              <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.primary}>{percentage}</Text>
              <PercentageSuffix>
                <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.primary}>%</Text>
              </PercentageSuffix>
            </PercentageWrapper>
          </ProgressBarLabelWrapper>
          <CountWrapper>
            <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text}>{formatNumber(group.totalCount)}</Text>
            <CountSuffix>
              <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.subText}>{t('statistics.engagement.memberUnit')}</Text>
            </CountSuffix>
          </CountWrapper>
        </ProgressBarInfoRow>
        <ProgressBarTrack>
          {
            group.totalCount > 0 &&
            <ProgressBarFill width={barWidth} />
          }
        </ProgressBarTrack>
      </ProgressBarCardWrapper>
    );
  }

  // 테이블 연령 그룹 행 렌더링 //
  function renderAgeGroupRow(group, index) {
    const percentage = calcPercentage(group.totalCount);
    const isLast = index === engagementStates.length - 1;
    const label = keyToLabel[group.key] || group.key;

    return (
      <AgeGroupRow key={'EngagementAgeGroup-' + index}>
        <AgeGroupLabelCell $isLast={isLast}>
          <AgeGroupLabelContent>
            <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text}>{label}</Text>
            <AgeGroupSubInfo>
              <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>
                {formatNumber(group.totalCount) + ' ' + t('statistics.engagement.memberUnit')}
              </Text>
              <PercentageWrapper>
                <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.primary}>{percentage}</Text>
                <PercentageSuffix>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.primary}>%</Text>
                </PercentageSuffix>
              </PercentageWrapper>
            </AgeGroupSubInfo>
          </AgeGroupLabelContent>
        </AgeGroupLabelCell>
        <MembershipColumnWrapper>
          {
            group.usages.map((usage, usageIndex) => (
              <TableDataCell
                key={'EngagementMembership-' + usageIndex}
                $isLast={isLast && usageIndex === group.usages.length - 1}
                $isGroupLast={usageIndex === group.usages.length - 1}>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{usage.membershipName}</Text>
              </TableDataCell>
            ))
          }
        </MembershipColumnWrapper>
        <UsageColumnWrapper>
          {
            group.usages.map((usage, usageIndex) => (
              <TableDataCell
                key={'EngagementUsage-' + usageIndex}
                $isLast={isLast && usageIndex === group.usages.length - 1}
                $isGroupLast={usageIndex === group.usages.length - 1}
                $center>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>
                  {formatNumber(usage.count) + ' ' + t('statistics.countUnit')}
                </Text>
              </TableDataCell>
            ))
          }
        </UsageColumnWrapper>
        <PreferenceColumnWrapper>
          {
            group.usages.map((usage, usageIndex) => (
              <TableDataCell
                key={'EngagementPreference-' + usageIndex}
                $isLast={isLast && usageIndex === group.usages.length - 1}
                $isGroupLast={usageIndex === group.usages.length - 1}
                $center>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>
                  {calcPreference(usage.count, group.reservedSum) + '%'}
                </Text>
              </TableDataCell>
            ))
          }
        </PreferenceColumnWrapper>
      </AgeGroupRow>
    );
  }

  return (
    <TopLevelWrapper>
      <IntroMarkdown text={intro} color={theme.text} />
      <ContentWrapper $isDark={isDark}>
        <SummaryRow>
          <AgeRangeImage src={AgeGroupIcon} />
          <Text fontFamily='NotoSansKR-Medium' fontSize={18} color={theme.text}>
            {t('statistics.engagement.mostAge') + ' : ' + (keyToLabel[engagementMaxKey] || engagementMaxKey)}
          </Text>
          <PercentageWrapper>
            <Text fontFamily='NotoSansKR-Medium' fontSize={18} color={theme.primary}>
              {engagementTotalCount > 0 ? (engagementMaxCount / engagementTotalCount * 100).toFixed(1) : '0'}
            </Text>
            <PercentageSuffix>
              <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.primary}>%</Text>
            </PercentageSuffix>
          </PercentageWrapper>
          <PeriodText fontFamily='NotoSansKR-Regular' fontSize={13} color={theme.subText}>
            {startDate + ' ~ ' + endDate}
          </PeriodText>
        </SummaryRow>
        <ProgressBarGridWrapper>
          <ProgressBarRow>
            {allAgeGroups.slice(0, 3).map((group, index) => renderProgressBarCard(group, index))}
          </ProgressBarRow>
          <ProgressBarRow>
            {allAgeGroups.slice(3, 6).map((group, index) => renderProgressBarCard(group, index + 3))}
          </ProgressBarRow>
          <ProgressBarRow>
            {allAgeGroups.slice(6, 9).map((group, index) => renderProgressBarCard(group, index + 6))}
          </ProgressBarRow>
        </ProgressBarGridWrapper>
        {
          engagementStates.length > 0 &&
          <TableSectionWrapper>
            <TableTitleWrapper>
              <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                {t('statistics.engagement.ageUsageStats')}
              </Text>
            </TableTitleWrapper>
            <TableContentWrapper>
              <TableHeaderRow>
                <TableHeaderCell width={160}>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>
                    {t('statistics.engagement.colAge')}
                  </Text>
                </TableHeaderCell>
                <TableHeaderCell width={300}>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>
                    {t('statistics.engagement.colMembership')}
                  </Text>
                </TableHeaderCell>
                <TableHeaderFlexCell>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>
                    {t('statistics.engagement.colUsageRecord')}
                  </Text>
                </TableHeaderFlexCell>
                <TableHeaderFlexCell>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>
                    {t('statistics.engagement.colPreference')}
                  </Text>
                </TableHeaderFlexCell>
              </TableHeaderRow>
              <TableDividerLine />
              <TableBodyWrapper>
                {engagementStates.map((group, index) => renderAgeGroupRow(group, index))}
              </TableBodyWrapper>
            </TableContentWrapper>
          </TableSectionWrapper>
        }
      </ContentWrapper>
    </TopLevelWrapper>
  );
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;

  gap: 12px;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;

  padding: 20px 24px;

  width: calc(100% - 48px);

  gap: 10px;

  border-radius: 12px;
  background-color: ${props => props.theme.subBackground};
`;

const SummaryRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  flex-wrap: wrap;

  width: 100%;

  gap: 8px;
`;

const PeriodText = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 auto;
  cursor: default;
`;

const AgeRangeImage = styled.img`
  width: 28px;
  height: 26px;

  margin-top: 2px;
`;

const PercentageWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  gap: 1px;
`;

const PercentageSuffix = styled.div`
  display: flex;
  align-items: flex-end;

  padding-bottom: 2px;
`;

const CountWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  overflow: hidden;

  gap: 5px;
`;

const CountSuffix = styled.div`
  display: flex;
  align-items: flex-end;

  padding-bottom: 2px;
`;

const ProgressBarGridWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  justify-content: center;

  padding: 12px 0;

  width: 100%;

  gap: 12px;
`;

const ProgressBarRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  width: 100%;

  gap: 32px;
`;

const ProgressBarCardWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  justify-content: center;

  flex: 1;
  min-width: 0;
`;

const ProgressBarInfoRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  width: 100%;

  gap: 10px;
`;

const ProgressBarLabelWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  overflow: hidden;

  flex: 1;
  min-width: 0;

  gap: 8px;
`;

const ProgressBarTrack = styled.div`
  position: relative;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  height: 8px;

  margin-top: 4px;

  border-radius: 1000px;
  background-color: ${props => props.theme.buttonFill3};
`;

const ProgressBarFill = styled.div`
  position: absolute;
  left: 0;
  top: 0;

  width: ${props => props.width}%;
  height: 8px;

  border-radius: 1000px;
  background-color: ${props => props.theme.primary};
`;

const TableSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;
  overflow: hidden;

  width: 100%;

  gap: 8px;
`;

const TableTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  justify-content: center;
  overflow: hidden;

  width: 100%;
`;

const TableContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  overflow: hidden;

  width: 100%;

  border-radius: 5px;
`;

const TableHeaderRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  overflow: hidden;

  width: 100%;
  height: 30px;

  border-radius: 5px 5px 0 0;
`;

const TableHeaderCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;

  padding: 0 10px;

  width: ${props => props.width}px;
`;

const TableHeaderFlexCell = styled.div`
  display: flex;
  flex: 1;

  align-items: center;
  justify-content: center;
  overflow: hidden;

  min-width: 0;
`;

const TableDividerLine = styled.div`
  width: 100%;
  height: 1px;

  margin: 5px 0;

  opacity: 0.5;
  background-color: ${props => props.theme.line};
`;

const TableBodyWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;

  width: 100%;
  max-height: 450px;

  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.scrollBar};
    border-radius: 200px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
    border-radius: 200px;
  }

  border-radius: 12px;
`;

const AgeGroupRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: stretch;

  width: 100%;
`;

const AgeGroupLabelCell = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;
  justify-content: center;
  overflow: hidden;

  padding: 16px 8px;

  width: 160px;

  background-color: ${props => props.theme.buttonFill};
  border-bottom: ${props => props.$isLast ? 'none' : `1px solid ${props.theme.primaryAlpha20}`};
`;

const AgeGroupLabelContent = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;

  padding: 8px 0;
`;

const AgeGroupSubInfo = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  gap: 5px;
`;

const MembershipColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  justify-content: center;

  width: 300px;

  border-right: 1px solid ${props => props.theme.line};
`;

const UsageColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  align-items: flex-start;
  justify-content: center;

  min-width: 0;

  border-right: 1px solid ${props => props.theme.line};
`;

const PreferenceColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  align-items: flex-start;
  justify-content: center;

  min-width: 0;
`;

const TableDataCell = styled.div`
  display: flex;
  flex: 1;

  align-items: center;
  justify-content: ${props => props.$center ? 'center' : 'flex-start'};
  overflow: hidden;

  padding: 10px;

  width: calc(100% - 20px);
  min-height: 0;

  border-bottom: ${props => {
    if (props.$isLast) return 'none';
    if (props.$isGroupLast) return `1px solid ${props.theme.primaryAlpha20}`;
    return `1px solid ${props.theme.line}`;
  }};
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0;
  cursor: default;
`;
